import React from 'react';

interface RelationSelectorWithPriorityProps {
  label: string;
  selectedIds: number[];
  options: Array<{ id: number; name: string }>;
  priorities: Record<number, number>;
  onChange: (ids: number[]) => void;
  onPriorityChange: (id: number, priority: number) => void;
  errors?: string[];
  columns?: number;
  showSelectAll?: boolean;
}

const RelationSelectorWithPriority: React.FC<RelationSelectorWithPriorityProps> = ({
  label,
  selectedIds,
  options,
  priorities,
  onChange,
  onPriorityChange,
  errors,
  columns = 3,
  showSelectAll = true,
}) => {
  const handleChange = (id: number, checked: boolean) => {
    if (checked) {
      onChange([...selectedIds, id]);
      // 新規選択時はデフォルト優先順位を設定
      if (!priorities[id]) {
        onPriorityChange(id, 1);
      }
    } else {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    }
  };

  const handleSelectAll = () => {
    const allIds = options.map((option) => option.id);
    onChange(allIds);
    // 全て選択時、優先順位が未設定のものはデフォルト値を設定
    allIds.forEach((id) => {
      if (!priorities[id]) {
        onPriorityChange(id, 1);
      }
    });
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handlePriorityChange = (id: number, value: string) => {
    const priority = parseInt(value, 10);
    if (!isNaN(priority) && priority > 0) {
      onPriorityChange(id, priority);
    }
  };

  const allSelected = options.length > 0 && options.every((option) => selectedIds.includes(option.id));

  const colClass = `col-md-${12 / columns}`;

  return (
    <div className="form-group">
      <div className="d-flex justify-content-between align-items-center mb-2">
        {label && (
          <label className="form-label fw-semibold mb-0">{label}</label>
        )}
        {showSelectAll && options.length > 0 && (
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={handleSelectAll}
              disabled={allSelected}
            >
              全て選択
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleClearAll}
              disabled={selectedIds.length === 0}
            >
              全て解除
            </button>
            <span className="text-muted small align-self-center">
              {selectedIds.length}/{options.length}件選択中
            </span>
          </div>
        )}
      </div>
      <div className="row">
        {options.map((option) => {
          const isSelected = selectedIds.includes(option.id);
          const priority = priorities[option.id] || 1;
          return (
            <div key={option.id} className={`${colClass} mb-2`}>
              <div className="form-check d-flex align-items-center gap-2">
                <input
                  className={`form-check-input ${errors ? 'is-invalid' : ''}`}
                  type="checkbox"
                  id={`relation_${option.id}`}
                  checked={isSelected}
                  onChange={(e) => handleChange(option.id, e.target.checked)}
                />
                <label className="form-check-label flex-grow-1" htmlFor={`relation_${option.id}`}>
                  {option.name}
                </label>
                {isSelected && (
                  <div className="d-flex align-items-center gap-1">
                    <label htmlFor={`priority_${option.id}`} className="form-label small mb-0 text-muted">
                      表示優先順位:
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      id={`priority_${option.id}`}
                      min="1"
                      value={priority}
                      onChange={(e) => handlePriorityChange(option.id, e.target.value)}
                      style={{ width: '70px' }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {errors && errors.length > 0 && (
        <div className="invalid-feedback d-block">{errors.join(', ')}</div>
      )}
    </div>
  );
};

export default RelationSelectorWithPriority;

